import axios from 'axios';
import { useAuthStore } from '../store/useAuthStore';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL
});

api.interceptors.request.use(
  (config) => {
    const isLoginRequest = config.url && config.url.includes('/auth/login');
    const isAcceptInviteRequest = config.url && config.url.includes('/auth/accept-invite');
    let token = useAuthStore.getState().token;

    if (token && typeof token === 'object') {
      token = token.accessToken || token.token || token.data;
    }

    if (token && !isLoginRequest && !isAcceptInviteRequest) {
      const headerValue = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      config.headers['Authorization'] = headerValue;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isVerifyingSession = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401 && originalRequest && !originalRequest._retry) {
      const isLoginPage = window.location.pathname === '/login';
      const isAcceptInvitePage = window.location.pathname === '/accept-invite';
      const isAuthRequest = originalRequest.url && (
        originalRequest.url.includes('/auth/login') ||
        originalRequest.url.includes('/auth/accept-invite')
      );
      const isAuthMeRequest = originalRequest.url && originalRequest.url.includes('/auth/me');

      if (isAuthRequest || isLoginPage || isAcceptInvitePage) {
        return Promise.reject(error);
      }

      if (isAuthMeRequest) {
        useAuthStore.getState().logout();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      // Prevent infinite loops for session check
      originalRequest._retry = true;

      if (!isVerifyingSession) {
        isVerifyingSession = fetchCurrentUser().then(res => {
          isVerifyingSession = null;
          return res;
        }).catch(err => {
          isVerifyingSession = null;
          throw err;
        });
      }

      try {
        await isVerifyingSession;
        // If verification succeeded, retry the original request once
        return api(originalRequest);
      } catch (verificationError) {
        // If verification fails, logout
        useAuthStore.getState().logout();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export const login = async (email, password) => {
  return api.post('/api/v1/portal/auth/login', { email, password });
};

export const fetchCurrentUser = async (token) => {
  const config = {};
  let tokenString = token;

  if (tokenString && typeof tokenString === 'object') {
    tokenString = tokenString.accessToken || tokenString.token || tokenString.data;
  }

  if (tokenString) {
    const headerValue = tokenString.startsWith('Bearer ') ? tokenString : `Bearer ${tokenString}`;
    config.headers = { 'Authorization': headerValue };
  }
  return api.get('/api/v1/portal/auth/me', config);
};

export const requestPasswordReset = async (email) => {
  return api.post('/api/v1/portal/auth/forgot-password', { email });
};

export const resetPassword = async (resetToken, newPassword) => {
  return api.post('/api/v1/portal/auth/reset-password', { resetToken, newPassword });
};

export const changePassword = async (data) => {
  return api.put('/api/v1/portal/auth/change-password', data);
};

export const acceptInvite = async (data) => {
  return api.post('/api/v1/portal/auth/accept-invite', data);
};


// Transaction APIs
export const fetchTransactionStats = async (params) => {
  return api.get('/api/v1/portal/transactions/stats', { params });
};

export const fetchTransactions = async (params) => {
  return api.get('/api/v1/portal/transactions/filter', { params });
};

export const fetchMerchantTransactions = async (params) => {
  return api.get(`/api/v1/portal/transactions`, { params });
};


// Product Management APIs
export const fetchAllProducts = async () => {
  return api.get('/api/v1/products/active');
};

export const fetchMerchantProducts = async () => {
  return api.get('/api/v1/portal/product-subscriptions/my-requests');
};

export const fetchMerchantDetails = async (merchantId) => {
  return api.get(`/api/v1/merchants/${merchantId}?includeApiKeys=true`);
};

export const subscribeToProducts = async (productCodes) => {
  return api.post('/api/v1/portal/product-subscriptions/request', { productCodes });
};

export const unsubscribeFromProducts = async (productCode) => {
  return api.delete(`/api/v1/portal/product-subscriptions/${productCode}`);
};

// Team Management APIs
export const fetchTeamMembers = async () => {
  return api.get('/api/v1/portal/team');
};

export const fetchTeamMemberDetails = async (userId) => {
  return api.get(`/api/v1/portal/team/${userId}`);
};

export const inviteTeamMember = async (data) => {
  return api.post('/api/v1/portal/team/invite', data);
};

export const updateTeamMember = async (userId, data) => {
  return api.put(`/api/v1/portal/team/${userId}`, data);
};

export const deactivateTeamMember = async (userId) => {
  return api.put(`/api/v1/portal/team/${userId}/deactivate`);
};

export const reactivateTeamMember = async (userId) => {
  return api.put(`/api/v1/portal/team/${userId}/reactivate`);
};

export const resendInvite = async (userId) => {
  return api.post(`/api/v1/portal/team/${userId}/resend-invite`);
};

export const requestLiveKey = async (data) => {
  return api.post('/api/v1/portal/keys/live/request', data);
};

export const fetchLiveKeyRequests = async () => {
  return api.get('/api/v1/portal/keys/live/requests');
};

export const regenerateTestKey = async (data) => {
  return api.post('/api/v1/portal/keys/test/regenerate', data);
};

// MERCHANTS API
export const fetchMerchantById = (id) => api.get(`/api/v1/portal/auth/me`);
export const getSettings = (merchantId) => api.get(`/api/v1/portal/settings`, { merchantId });
export const getCardEncryptionSettings = () => api.get('/api/v1/portal/settings/encryption-key');
export const updateCardEncryptionSettings = (data) => api.put('/api/v1/portal/settings/encryption-key', data);
export const updateMerchantWebhook = (merchantId, data) => api.put(`/api/v1/portal/settings/webhook`, data);
export const updateMerchantKey = (merchantId, data) => api.put(`/api/v1/portal/settings/security-key`, { merchantId, ...data });

// System Logs
export const fetchSystemLogs = async (params) => {
  return api.get('/api/v1/portal/analytics/activities/search', { params });
};


export default api;
